import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { GRADEANDLEXILELEVELS } from "@/constants/GradeAndLexileLevels";
import VISIBILITY_MARKER_REASONS from "@/constants/VISIBILITY_MARKER_REASONS";
import { convertUTCStringToLocalTime } from "./dateUtils";

// consts:
export const assigneeSelectionSteps = {
  ASSIGNEE_TYPE_SELECTION_STEP: 0,
  CLASSROOMS_SELECTION_STEP: 1,
  STUDENTS_SELECTION_STEP: 2,
  DESELECT_CONFIRMATION_STEP: 3,
};
export const ASSIGNMENT_VERSION_LEGACY = "legacy";
export const ASSIGNMENT_VERSION_MVE = "single_asset_mve";

// SRC (Student Restricted Content)
// Content could be marked by either a district or a school.
const SRC_MARKERS = [
  VISIBILITY_MARKER_REASONS.DISTRICT_ADVISORY,
  VISIBILITY_MARKER_REASONS.SCHOOL_ADVISORY,
];

/**
 * @param {Record<string, string | number | boolean | null | undefined>} params - Object containing Assignments API params.
 * @param {boolean | undefined} isTeacher - Indicates if the current user is a teacher.
 * @return object containing only the original Assignments API params that are valid for the current user type, all other params will be omitted
 */
export const sanitizeQueryParamsForUser = (params, isTeacher) => {
  const sanitizedParams = params;

  /* remove params that are invalid for non-teachers */
  if (!isTeacher) {
    delete sanitizedParams.include_drafts;
    delete sanitizedParams.with_archived_classrooms;
    delete sanitizedParams.excluded_roles;
  }
  return sanitizedParams;
};
/**
 * @param {number} grade - Grade number needed to format grade level and lexile level
 * @return array containing properly formatted grade level and lexile level strings
 */
export const gradeLevelFormatter = (grade) => {
  return [
    GRADEANDLEXILELEVELS[grade]?.formattedGrade,
    GRADEANDLEXILELEVELS[grade]?.lexileRange,
  ];
};

/**
 * @param {object[]} articles - Articles contained in the assignment
 * @return sorted and deduped array containing all grade and lexile levels for given articles
 */
export const getLexileLevelArray = (articles) => {
  const lexileLevels = [];

  articles?.forEach((article) => {
    const unsortedGrades = [...article?.grade_levels];
    unsortedGrades.sort((a, b) => b - a);

    const gradeLevels = unsortedGrades.map((level) => {
      const [gradeLevel] = gradeLevelFormatter(level);

      return gradeLevel;
    });

    lexileLevels.push(...gradeLevels.filter((level) => level !== undefined));
  });

  return ["No level locking", ...new Set(lexileLevels)];
};

export const urlFormatter = (returnURL) => {
  let formattedURL = returnURL;

  const splitStr = returnURL.split("");
  if (splitStr[0] === "/") {
    formattedURL = returnURL.replace("/", "");
  } else if (splitStr[0] + splitStr[1] + splitStr[2] === "%2F") {
    formattedURL = splitStr.slice(3, -1).join("");
  }

  return formattedURL;
};

export const isAllowedUrl = (originalURL) => {
  const url = new URL(originalURL);
  const domains = ["newsela.com", "newseladev.com"];
  const urlDomain = url.hostname;
  if (domains.includes(urlDomain)) {
    return true;
  } else if (
    domains.some((allowed) => urlDomain.endsWith(".".concat(allowed)))
  ) {
    return true;
  } else {
    return false;
  }
};

export const flattenAssignmentMembers = (
  assignmentMembers,
  maxLimit = Infinity,
) => {
  const seenContent = {};
  let hitMaxLimit = false;

  function flatten({ members }, result = []) {
    for (let i = 0, length = members.length; i < length; i++) {
      hitMaxLimit = hitMaxLimit || result.length >= maxLimit;
      if (hitMaxLimit) {
        break;
      }

      const item = members[i];
      const keepRecursing =
        Array.isArray(item.members) &&
        item.content_type === "textset" &&
        !seenContent[item.content_id];

      if (keepRecursing) {
        flatten(item, result);
      } else if (!seenContent[item.content_id]) {
        result.push(item);
      }
      seenContent[item.content_id] = true;
    }

    return result;
  }

  return [flatten({ members: assignmentMembers }), hitMaxLimit];
};

function getDelimiter(displayDelimiter, delimiter = ", ") {
  return displayDelimiter ? delimiter : "";
}

export function getFullUserName({
  first_name: firstName = "",
  last_name: lastName = "",
}) {
  return `${firstName}${firstName && lastName ? " " : ""}${lastName}`;
}

function getSmallGroupUserNames(users = []) {
  return users.map(getFullUserName).join(", ");
}

function getOtherUsersDescription(otherUsersCount, displayDelimiter) {
  if (otherUsersCount === 0) return "";

  const delimiter = getDelimiter(displayDelimiter, " ");
  const isPlural = otherUsersCount > 1;

  return `${delimiter}and ${otherUsersCount} more student${isPlural ? "s" : ""}`;
}

function getFirstNthUsers(selectedUsers) {
  const firstNthUsers = [];
  const usersWithoutNameValues = [];

  selectedUsers.forEach(function (user) {
    const hasNameValues = user.first_name && user.last_name;
    const arr = hasNameValues ? firstNthUsers : usersWithoutNameValues;

    arr.push(user);
  });

  return { firstNthUsers, usersWithoutNameValues };
}

export const getTruncatedSmallGroupUserNames = (users = [], n = 2) => {
  if (users.length === 0) return "No users selected";

  const selectedUsers = users.slice(0, n);
  const { firstNthUsers, usersWithoutNameValues } =
    getFirstNthUsers(selectedUsers);

  const otherUsers = users.slice(n, users.length);
  const otherUsersCount = otherUsers.length + usersWithoutNameValues.length;

  const firstNthUserNames = getSmallGroupUserNames(firstNthUsers);
  const hasSingleNthUser = firstNthUsers.length === 1;
  const hasNoOtherUsers = otherUsersCount === 0;
  const omitDelimiter = hasSingleNthUser || hasNoOtherUsers;
  const delimiter = getDelimiter(!omitDelimiter);
  const otherUsersDescription = getOtherUsersDescription(
    otherUsersCount,
    hasSingleNthUser,
  );

  return `${firstNthUserNames}${delimiter}${otherUsersDescription}`;
};

export const getAssignmentContentIds = (assignments = []) => {
  return assignments.map((assignment) => assignment?.content_id);
};

export const getFlattenedClassrooms = (classrooms = []) => {
  if (!Array.isArray(classrooms)) return [];

  return classrooms.map(({ classroom = {}, ...rest }) => {
    return { ...rest, ...classroom };
  });
};

export const getExpirationDate = (asset = {}, isLiteUser) => {
  const { date_lite_product_grant_expires: expirationDate } = asset;
  // If no date provided, or user is not lite user, we do not set an exp date
  if (!expirationDate || !isLiteUser) {
    return null;
  }
  const formattedExpDate = convertUTCStringToLocalTime(expirationDate);
  return formattedExpDate !== "Invalid Date" ? formattedExpDate : null;
};

export const getLiteContent = (contentArray = []) => {
  return contentArray.filter(
    (content) => content?.is_lite && content?.date_lite_product_grant_expires,
  );
};

export const getLiteContentWithEarliestExpireDate = (contentArray = []) => {
  const liteContent = getLiteContent(contentArray);
  const firstContentObject = liteContent[0];

  if (!firstContentObject) return {};
  if (liteContent.length === 1) return firstContentObject;

  // compare expire dates for each content object
  dayjs.extend(isSameOrBefore); // set up dayjs
  let contentWithEarliestExpireDate = firstContentObject;
  for (const content of contentArray) {
    const dateToCompare = content.date_lite_product_grant_expires;
    const referenceDate =
      contentWithEarliestExpireDate.date_lite_product_grant_expires;

    if (dayjs(dateToCompare).isSameOrBefore(referenceDate)) {
      contentWithEarliestExpireDate = content;
    }
  }

  return contentWithEarliestExpireDate;
};

export const getIsDistrictAdvisory = (content) => {
  // expecting content object from content-document
  const reason = content?.veiled?.[0]?.reason;
  const isDistrictAdvisory = reason === "DISTRICT_ADVISORY";
  return isDistrictAdvisory;
};

export const getIsContentStudentRestricted = (content = {}) => {
  // expects either content api object or langston data (content-document api)
  // the following is temporary logic used to trigger the true and false branches of user stories surrounding SRC
  // should allow QA and devs to control the return value by passing in `isSRC` into the search params. Ex: `?isSRC=true`
  if (typeof window === "undefined") {
    return false;
  }
  const queryParams = {};
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    queryParams[key] = value;
  }

  // content-document object will have `data` key, content api object will not. Will use as discriminator for time being.
  // extract marker from either langston or content api object:
  const markers =
    content?.visibility_markers ||
    content?.data?.visibility_markers ||
    content?.content?.visibility_markers ||
    [];
  const isStudentRestricted = markers.some((marker = {}) =>
    SRC_MARKERS.includes(marker.reason),
  );

  return isStudentRestricted || queryParams.isSRC;
};

export const getGradeBandRanges = (visibilityMarkers = []) => {
  // Expects array of groups from visibility/content-settings-groups
  // Returns array of ranges in the following format: ['min-max', 'min-max', 'min-max']
  const sortedGroups = visibilityMarkers.sort(
    (curr, next) => curr.grade_minimum - next.grade_minimum,
  );

  return sortedGroups.map((group) => {
    const { grade_minimum: minGrade, grade_maximum: maxGrade } = group || {};
    if (minGrade === maxGrade) {
      return `${minGrade || "K"}`;
    }
    return `${minGrade || "K"}-${maxGrade || "K"}`;
  });
};

export const getAllRestrictedBandsCopy = (visibilityMarkers = []) => {
  // Expects array of groups from visibility/content-settings-groups
  // Returns copy with list of restricted grades, calculates correct punctuation and wording based on input
  const grades = getGradeBandRanges(visibilityMarkers);
  let [first, ...rest] = grades;
  if (!first) {
    return null;
  }
  const [last] = rest.splice(-1);
  const csv = rest.join(", ") ? `, ${rest.join(", ")}` : "";
  if (first.length === 1 && !last) {
    first = `grade ${first}`;
  } else {
    first = `grades ${first}`;
  }
  return `This advisory applies to ${first}${last ? `${csv || ""} and ${last}.` : "."}`;
};

export const getConSetVisibilityMarkers = (content = []) => {
  // Expects array of content from either Content API or Langston
  // Returns an array of visibility markers
  const contentWithAdvisoryMarkers = content
    .filter((c) => getIsContentStudentRestricted(c))
    .map((c) => c.data || c);
  const markers = contentWithAdvisoryMarkers.map((content) => {
    // TODO: Switch to types after they are are introduced.
    return content.visibility_markers?.filter(
      (marker) =>
        marker.reason === "DISTRICT_ADVISORY" ||
        marker.reason === "SCHOOL_ADVISORY",
    );
  });
  return markers.flat();
};

const needsConSetOverride = (content = {}, isConsented) => {
  const { user_content_settings_override: userContentSettingsOverride } =
    content.data || content?.content || content;

  return (
    getIsContentStudentRestricted(content) &&
    Boolean(userContentSettingsOverride) === isConsented
  );
};

export const getHasSRCContent = (contentItems) => {
  if (!Array.isArray(contentItems)) return false;

  return contentItems.some((content = {}) =>
    getIsContentStudentRestricted(content?.content || content),
  );
};

export const getHasUnconsentedSRC = (contentItems) => {
  if (!Array.isArray(contentItems)) return false;

  return contentItems.some((content) =>
    needsConSetOverride(content?.content || content, false),
  );
};

export const getConsentedSRCContent = (contentItems) => {
  if (!Array.isArray(contentItems)) return [];

  return contentItems.filter((content) =>
    needsConSetOverride(content?.content || content, true),
  );
};

export const getUnconsentedSRCContent = (contentItems) => {
  if (!Array.isArray(contentItems)) return [];

  return contentItems.filter((content) =>
    needsConSetOverride(content?.content || content, false),
  );
};

export const determineSRCState = (contentItems = []) => {
  // utility for SRC, student restricted content, for Content Settings. This should not be used for CAT.
  const containsSRC = getHasSRCContent(contentItems);
  const containsUnconsentedSRC = getHasUnconsentedSRC(contentItems);
  const unconsentedSRCContent = getUnconsentedSRCContent(contentItems);
  const consentedSRCContent = getConsentedSRCContent(contentItems);
  const SRCState = {
    containsSRC,
    containsUnconsentedSRC,
    unconsentedSRCContent,
    consentedSRCContent,
  };

  // the following is temporary logic used to trigger desired SRCState to test/qa user stories surrounding SRC
  // this should allow QA and devs to control the return value by passing in memberNeedsConsent={assetContentId} (found in contentItem.content_id)
  if (typeof window === "undefined") {
    return SRCState;
  }
  const queryParams = {};
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    queryParams[key] = value;
  }

  if (queryParams.memberNeedsConsent) {
    const mockState = {
      containsSRC: contentItems.some(
        (item = {}) => item?.content_id === queryParams.memberNeedsConsent,
      ),
      containsUnconsentedSRC: contentItems.some(
        (item = {}) =>
          item?.content_id === queryParams.memberNeedsConsent &&
          !item?.user_content_settings_override,
      ),
      unconsentedSRCContent: contentItems.filter(
        (item = {}) =>
          item?.content_id === queryParams.memberNeedsConsent &&
          !item?.user_content_settings_override,
      ),
      consentedSRCContent: contentItems.filter(
        (item = {}) =>
          item?.content_id === queryParams.memberNeedsConsent &&
          item?.user_content_settings_override,
      ),
    };
    return mockState;
  }

  return SRCState;
};

export const determineSRCStateForMembers = (assignmentMembers = []) => {
  const contentItems = assignmentMembers.map((member) => member.content);
  const state = determineSRCState(contentItems);
  state.unconsentedSRCContent = state.unconsentedSRCContent.map((content) => ({
    content,
  }));
  state.consentedSRCContent = state.consentedSRCContent.map((content) => ({
    content,
  }));
  return state;
};

export const getAssignmentContainsExpired = (assignment = {}) => {
  // expects assignment object
  // the following is temporary logic used to trigger the true and false branches of user stories surrounding SRC
  // should allow QA and devs to control the return value by passing in `expiredAssignment` into the search params with an assignment content id.
  // Ex: `?expiredAssignment=clhgj6alm000020p0qvmkq11d`
  if (typeof window === "undefined") {
    return false;
  }
  const queryParams = {};
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    queryParams[key] = value;
  }

  // TODO: Should look into assignment for evidence of a member being expired. Currently blocked on this.
  const containsExpiredMember =
    queryParams.expiredAssignment === assignment.content_id;
  return containsExpiredMember;
};

export const getAssignmentLink = (assignmentId, contentId) => {
  return `${window.location.origin}/view/${contentId}/?selectedAssignmentId=${assignmentId}`;
};

export const getPrintAssignmentLink = (assignmentId, contentLevelId) => {
  return `${window.location.origin}/content/${assignmentId}/print/?format=pdf&article_level=${contentLevelId}`;
};

export const getAssignmentsListPageLink = () => {
  return `${window.location.origin}/binder/assignments`;
};

export const getIsPublishedAssignment = (assignment) => {
  return Boolean(assignment?.content_id) && !assignment?.is_draft;
};
